import React, { useEffect, useRef, useState } from "react";
import {
  buildNotePreview,
  deriveNoteTitle,
  getNoteAccentColor,
  getNoteAttachmentCount,
} from "../../utils/myNotes";
import NoteAttachmentIndicator from "./NoteAttachmentIndicator";
import NoteSubjectTag from "./NoteSubjectTag";

function NoteCard({ note, onOpen, onEdit, onDelete }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const visibleTags = (note.tags || []).slice(0, 2);
  const hiddenTagCount = Math.max((note.tags || []).length - visibleTags.length, 0);
  const attachmentCount = getNoteAttachmentCount(note);
  const accentColor = note.accentColor || getNoteAccentColor(note);
  const cardTitle = deriveNoteTitle(note.title, note.content, {
    attachments: note.attachments,
  });

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  return (
    <article
      className="my-notes-card"
      onClick={() => onOpen?.(note)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen?.(note);
        }
      }}
      role="button"
      tabIndex={0}
    >
      <span
        className="my-notes-card-accent"
        style={{ backgroundColor: accentColor }}
        aria-hidden="true"
      />

      <div className="my-notes-card-top">
        <div className="my-notes-card-title-row">
          <h3>{cardTitle}</h3>
          {attachmentCount > 0 ? (
            <NoteAttachmentIndicator count={attachmentCount} className="on-card" />
          ) : null}
        </div>
        <div className="my-notes-card-menu" ref={menuRef}>
          <button
            type="button"
            className="my-notes-card-menu-button"
            aria-label={`Open actions for ${cardTitle}`}
            onClick={(event) => {
              event.stopPropagation();
              setMenuOpen((open) => !open);
            }}
          >
            ⋮
          </button>
          {menuOpen ? (
            <div className="my-notes-card-menu-dropdown">
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  setMenuOpen(false);
                  onEdit?.(note);
                }}
              >
                Edit
              </button>
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  setMenuOpen(false);
                  onDelete?.(note);
                }}
              >
                Delete
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <p className="my-notes-card-preview">{buildNotePreview(note)}</p>

      <div className="my-notes-card-tags">
        {visibleTags.map((tag) => (
          <NoteSubjectTag key={tag} tag={tag} className="on-card" />
        ))}
        {hiddenTagCount > 0 ? (
          <span className="my-notes-card-tag my-notes-card-tag-more">+{hiddenTagCount}</span>
        ) : null}
      </div>
    </article>
  );
}

export default NoteCard;
